import { Link } from 'react-router-dom'
import { ApiError } from '../lib/api'
import { useAdminMe } from '../components/RequireAdmin'

/**
 * 사이트맵 — 이 플랫폼에 있는 **모든 화면**을 한 장에 편다.
 *
 * 만든 이유: 화면 수보다 헤더 메뉴(08-14 BI 시안 6개)가 훨씬 적어, 메뉴만 봐서는
 * 도달할 수 없는 화면이 많다. 등록·수정 폼처럼 목록을 거쳐야만 닿는 화면들이 그렇다.
 * 사내에서 진행 상황을 훑거나 발주기관이 검토할 때 여기부터 보면 된다.
 *
 * **이 목록은 `App.tsx`의 `<Routes>`와 1:1로 맞춘다.** 라우트를 추가하면 여기도 같이 추가한다 —
 * 어긋나면 사이트맵이 거짓말을 한다. E2E(`tests/e2e/sitemap.spec.ts`)가 그 대조를 강제한다.
 */

type ScreenNode = {
  path: string
  label: string
  desc: string
  /**
   * 경로에 `:id` 같은 파라미터가 있어 **직접 열 수 없는** 화면. 목록에서 항목을 골라 들어간다.
   * 링크로 만들면 깨진 주소가 되므로 경로만 보여주고 진입 경로를 적는다.
   */
  via?: string
  /** 아직 만들지 않은 화면. 있는 것처럼 보이지 않게 그대로 표기한다. */
  pending?: boolean
  children?: ScreenNode[]
}

/** 공개 화면 — 로그인 없이 누구나 본다. */
const PUBLIC_TREE: ScreenNode[] = [
  {
    path: '/',
    label: '홈',
    desc: '최근 등록된 시민문제카드·기업해결카드를 보여준다. 헤더 로고가 여기로 온다.',
  },
  {
    path: '/about',
    label: '소개',
    desc: '해봄이 무엇을 하는 곳인지, 어떻게 참여하고 등록 뒤에 어떻게 되는지.',
  },
  {
    path: '/problems',
    label: '시민문제카드',
    desc: '시민이 등록한 문제(시민문제카드) 목록. 분야·대상·동으로 거른다.',
    children: [
      {
        path: '/problems/new',
        label: '문제 등록',
        desc: '시민이 한 문장으로 불편을 접수한다. 회원가입 없이 익명 세션으로 묶는다.',
      },
      {
        path: '/problems/:id',
        label: '문제 상세',
        desc: '접수된 문제 하나의 내용과 상태.',
        via: '시민 문제 목록에서 항목 선택',
      },
    ],
  },
  {
    path: '/solutions',
    label: '기업해결카드',
    desc: '기업이 등록한 해결카드 목록. 매칭 단위는 기업이 아니라 개별 해결카드다.',
    children: [
      {
        path: '/solutions/:id',
        label: '해결카드 상세',
        desc: '해결카드 한 건의 도움방식·분야·대상·서비스 지역.',
        via: '기업카드 목록에서 항목 선택',
      },
      {
        path: '/orgs/:id',
        label: '기업 상세',
        desc: '기업정보와 그 기업이 가진 해결카드 목록(1기업 : N해결카드).',
        via: '해결카드 상세에서 기업명 선택',
      },
    ],
  },
  {
    path: '/map',
    label: '해봄지도',
    // 실행성과 레이어만 아직 데이터가 없다 — 화면 자체는 열린다. 그 상태는 지도 안에서 밝힌다.
    desc: '수요·해결역량·미충족·실행성과를 동 단위로 봅니다(실행성과는 데이터 준비 중).',
  },
  {
    path: '/board',
    label: '소식·이야기',
    // 헤더는 이 한 화면을 `?board=`로 갈라 `이야기`·`소식` 두 메뉴로 낸다(App.tsx MENU).
    // 사이트맵은 라우트를 적는 곳이므로 화면 하나로 적고 그 사실을 밝힌다.
    desc: '관리자가 올리는 글. 헤더의 `이야기`(`?board=org_visit`)·`소식`(`?board=notice`)이 같은 화면을 게시판별로 연다.',
    children: [
      {
        path: '/board/:id',
        label: '글 상세',
        desc: '글 본문과 첨부 이미지.',
        via: '소식·이야기 목록에서 항목 선택',
      },
    ],
  },
  {
    path: '/privacy',
    label: '개인정보 처리방침',
    desc: '무엇을 왜 받고 언제까지 갖는지. 게시 주체는 주식회사 이티랩(보호법 제30조).',
  },
  {
    path: '/sitemap',
    label: '사이트맵',
    desc: '지금 보고 있는 이 화면.',
  },
  {
    path: '(그 외 모든 주소)',
    label: '찾는 화면이 없습니다',
    desc: '없는 주소로 들어왔을 때 나오는 안내 화면.',
    via: '잘못된 주소 입력 시 자동',
  },
]

/** 관리자 화면 — 로그인해야 열린다. 차단은 서버가 하고 화면은 안내만 한다. */
const ADMIN_TREE: ScreenNode[] = [
  {
    path: '/admin/login',
    label: '관리자 로그인',
    desc: '아이디·비밀번호로 관리자 세션을 만든다. 로그인 없이도 열리는 유일한 관리자 화면이다.',
  },
  {
    path: '/admin',
    label: '카드 검수',
    desc: '등록된 카드를 공개할지 판단하고 상태를 전이한다. 전이 규칙은 서버가 정본이다.',
    children: [
      {
        path: '/admin/problems/:id',
        label: '시민문제 확정',
        desc: 'AI가 채운 공개 요약을 관리자가 확인·수정하고 공개로 넘긴다.',
        via: '카드 검수 목록에서 시민문제 선택',
      },
    ],
  },
  {
    path: '/admin/orgs',
    label: '기업 관리',
    desc: '비공개를 포함한 기업정보 목록. 여기서 기업과 해결카드를 만든다.',
    children: [
      { path: '/admin/orgs/new', label: '새 기업', desc: '기업정보를 등록한다.' },
      {
        path: '/admin/orgs/:id',
        label: '기업 수정',
        desc: '등록된 기업정보를 고친다.',
        via: '기업 관리 목록에서 항목 선택',
      },
      {
        path: '/admin/cards/new',
        label: '새 해결카드',
        desc: '기업에 속한 해결카드를 등록한다. 소속 기업은 주소의 orgId로 받는다.',
      },
      {
        path: '/admin/cards/:id',
        label: '해결카드 수정',
        desc: '등록된 해결카드를 고친다.',
        via: '기업 상세·검수 화면에서 항목 선택',
      },
    ],
  },
  {
    path: '/admin/match',
    label: '매칭',
    desc: '시민문제카드에 해결카드 후보를 붙인다. 제외조건 필터 → 적합도 100점 2단계.',
    children: [
      {
        path: '/admin/match/unmet',
        label: '미충족 큐',
        desc: '후보가 없거나 전원 제외된 문제를 모아 보여준다. 여기서 다음 행동을 고른다.',
      },
      {
        path: '/admin/match/:id',
        label: '매칭 결과',
        desc: '후보 목록과 점수 근거, 상태 전이.',
        via: '매칭 목록에서 실행 건 선택',
      },
    ],
  },
  {
    path: '/admin/data',
    label: '데이터 관리',
    desc: '카드·기업 데이터를 검색·필터해 CSV/XLSX로 내보낸다.',
  },
  {
    path: '/admin/posts',
    label: '소식 관리',
    desc: '소식 글을 쓰고 공개·삭제한다.',
    children: [
      { path: '/admin/posts/new', label: '새 소식', desc: '글을 작성하고 이미지를 첨부한다.' },
      {
        path: '/admin/posts/:id',
        label: '소식 수정',
        desc: '작성된 글을 고친다.',
        via: '소식 관리 목록에서 항목 선택',
      },
    ],
  },
  {
    path: '/admin/weights',
    label: '배점 조정',
    desc: '매칭 배점(6항목)을 조정하고 되돌린다. 항목은 계약 구조라 늘리거나 줄이지 않는다.',
    via: '매칭 화면의 「배점 조정」',
  },
  {
    path: '/admin/settings',
    label: '설정',
    desc: 'AI 입력보조의 API 키·모델을 관리한다. 저장값이 서버 기본값(env)보다 우선한다.',
  },
  {
    path: '/admin/dev',
    label: '개발자 패널',
    desc: '구축 담당자용 상태·감사 화면. dev:access 권한 계정으로만 열린다.',
  },
]

function countScreens(nodes: ScreenNode[]): number {
  return nodes.reduce((sum, n) => sum + 1 + (n.children ? countScreens(n.children) : 0), 0)
}

function ScreenItem(props: { node: ScreenNode; linkable: boolean }) {
  const { node, linkable } = props
  // 파라미터가 있거나 관리자 미로그인이면 링크로 만들지 않는다 — 눌러도 못 가는 링크를 두지 않는다.
  const isLink = linkable && node.via === undefined && !node.pending

  return (
    <li className="sitemap-item">
      <div className="sitemap-item-head">
        {isLink ? (
          <Link className="sitemap-label" to={node.path}>
            {node.label}
          </Link>
        ) : (
          <span className="sitemap-label off">{node.label}</span>
        )}
        <code className="sitemap-path">{node.path}</code>
        {node.pending === true && <span className="badge pre">준비 중</span>}
      </div>
      <p className="sitemap-desc">{node.desc}</p>
      {node.via !== undefined && <p className="sitemap-via">진입 경로 — {node.via}</p>}
      {node.children !== undefined && (
        <ul className="sitemap-children">
          {node.children.map((c) => (
            <ScreenItem key={c.path} node={c} linkable={linkable} />
          ))}
        </ul>
      )}
    </li>
  )
}

export function SitemapPage() {
  const me = useAdminMe()
  // 401은 "아직 로그인 안 함"이라는 정상 상태다(RequireAdmin과 같은 판정).
  const isLoggedIn = me.data !== undefined
  const isUnauthorized = me.error instanceof ApiError && me.error.status === 401

  const publicCount = countScreens(PUBLIC_TREE)
  const adminCount = countScreens(ADMIN_TREE)

  return (
    <div className="panel">
      <h1 className="page-title">사이트맵</h1>
      <p className="page-desc">
        해봄에 있는 화면 전체입니다. 공개 {publicCount}개 · 관리자 {adminCount}개, 모두{' '}
        {publicCount + adminCount}개입니다. 메뉴에 없는 화면(등록·수정 폼처럼 목록을 거쳐야 닿는
        것)도 여기에 다 적었습니다.
      </p>

      <section className="sitemap-section">
        <h2 className="section-title">공개 화면 ({publicCount})</h2>
        <p className="muted small">로그인 없이 누구나 볼 수 있습니다.</p>
        <ul className="sitemap-tree">
          {PUBLIC_TREE.map((n) => (
            <ScreenItem key={n.path} node={n} linkable />
          ))}
        </ul>
      </section>

      <section className="sitemap-section">
        <h2 className="section-title">관리자 화면 ({adminCount})</h2>
        {me.isPending && <p className="muted small">로그인 상태 확인 중…</p>}
        {isLoggedIn && (
          <p className="muted small">
            <b>{me.data?.name}</b> 님으로 로그인되어 있어 바로 이동할 수 있습니다.
          </p>
        )}
        {isUnauthorized && (
          <div className="notice">
            로그인하지 않아 링크가 꺼져 있습니다. <Link to="/admin/login">관리자 로그인</Link> 후
            이 화면으로 돌아오면 살아납니다.
            <br />
            <span className="muted small">
              화면을 감추는 것은 보안 장치가 아닙니다 — 실제 차단은 서버가 합니다.
            </span>
          </div>
        )}
        <ul className="sitemap-tree">
          {ADMIN_TREE.map((n) => (
            // 로그인 화면은 로그인 상태와 무관하게 열린다.
            <ScreenItem key={n.path} node={n} linkable={isLoggedIn || n.path === '/admin/login'} />
          ))}
        </ul>
      </section>
    </div>
  )
}
